import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../../utils/api";
import { fetchTeachers } from "./dataService";
import { DashboardIcon } from "../../shared/icons/Icons";
import "./CourseCampusSelectPage.css";

const TEACHERS_KEY = "als_teachers";

function readLocalStorageList(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch (err) {
    console.error(`Failed to parse localStorage key "${key}"`, err);
    return fallback;
  }
}

function mergeSavedTeacherAssignments(teachers, savedTeachers) {
  const savedById = new Map(
    (Array.isArray(savedTeachers) ? savedTeachers : []).map((teacher) => [
      String(teacher.id),
      teacher,
    ]),
  );

  return (Array.isArray(teachers) ? teachers : []).map((teacher) => {
    const savedTeacher = savedById.get(String(teacher.id));
    return {
      ...teacher,
      assigned_courses: Array.isArray(savedTeacher?.assigned_courses)
        ? savedTeacher.assigned_courses
        : Array.isArray(teacher.assigned_courses)
          ? teacher.assigned_courses
          : [],
    };
  });
}

function countTeachersByCampus(teachers) {
  return teachers.reduce((acc, teacher) => {
    const campusId = teacher?.campus_id;
    if (campusId != null && campusId !== "") {
      const key = String(campusId);
      acc[key] = (acc[key] || 0) + 1;
    }
    return acc;
  }, {});
}

// This component is intentionally generic so it can be reused for any
// "pick a campus, then go do something" flow. Course assignment uses the
// defaults below; Timetable reuses the exact same component by passing a
// different basePath/title (see DashboardLayout.js).
function CourseCampusSelectPage({
  onLogout,
  showToast,
  basePath = "/teachers/courses/campus",
  title = "Select Campus",
  backTo = "/dashboard",
}) {
  const [campuses, setCampuses] = useState([]);
  const [teacherCounts, setTeacherCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCampuses = async () => {
      try {
        const token = localStorage.getItem("als_token");
        const campusRes = await fetch(`${API_BASE_URL}/api/campuses`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (campusRes.status === 401) {
          onLogout();
          return;
        }

        if (!campusRes.ok) {
          throw new Error("Failed to fetch campuses");
        }

        const data = await campusRes.json();
        const list = Array.isArray(data) ? data : [];
        setCampuses(list);

        // GET /api/campuses is already scoped to the caller, so an admin tied to
        // a single campus has nothing to choose — jump straight to the real page.
        if (list.length === 1) {
          navigate(`${basePath}/${list[0].id}`, { replace: true });
          return;
        }
      } catch (err) {
        console.error("Failed to fetch campuses", err);
        showToast &&
          showToast("error", "Load Failed", "Could not load campuses.");
      } finally {
        setLoading(false);
      }
    };

    const loadTeacherCounts = async () => {
      const savedTeachers = readLocalStorageList(TEACHERS_KEY, []);
      let teacherList = savedTeachers;

      try {
        const backendTeachers = await fetchTeachers();
        if (Array.isArray(backendTeachers)) {
          teacherList = mergeSavedTeacherAssignments(
            backendTeachers,
            savedTeachers,
          );
        }
      } catch (err) {
        if (err?.status === 401) {
          onLogout();
          return;
        }
        console.warn(
          "Failed to load teachers from API, falling back to local teacher cache.",
          err,
        );
      }

      setTeacherCounts(countTeachersByCampus(teacherList));
    };

    loadTeacherCounts();
    fetchCampuses();
  }, [onLogout, showToast, navigate, basePath]);

  if (loading) {
    return <div className="page-loading">Loading campuses...</div>;
  }

  return (
    <div className="campus-select-page">
      <div className="campus-select-header">
        <button
          className="campus-select-back-btn"
          onClick={() => navigate(backTo)}
        >
          ← Back
        </button>
        <h2>{title}</h2>
      </div>

      <div className="campus-select-grid">
        {campuses.map((campus) => (
          <button
            key={campus.id}
            className="campus-select-card"
            onClick={() => navigate(`${basePath}/${campus.id}`)}
          >
            <div className="campus-select-card-title">
              <span className="campus-select-card-icon">
                <DashboardIcon />
              </span>
              <span className="campus-select-card-name">{campus.name}</span>
            </div>

            <div className="campus-select-card-meta">
              <p>
                <strong>Location:</strong> {campus.location || "—"}
              </p>
              <p>
                <strong>Principal:</strong>{" "}
                {campus.principal_name || campus.principal || "—"}
              </p>
              <p className="campus-select-teacher-count">
                <strong>Teachers:</strong>{" "}
                {teacherCounts[String(campus.id)] || 0}
              </p>
            </div>
          </button>
        ))}
      </div>

      {campuses.length === 0 && (
        <div className="campus-select-empty">No campuses found.</div>
      )}
    </div>
  );
}

export default CourseCampusSelectPage;
